import type { NarrativeDelta, NarrativeInvalidation, StoryApiError } from "@xhalo/story-contracts";

export type StudioAuth = { previewActor?: string; previewDisplayName?: string; branchName?: string };

export type StoryProject = {
  id: string;
  name: string;
  formatProfile: string;
  status: string;
  updatedAt: number;
  branch: { id: string; headCommitId: string };
};

export type ContextPackInspection = {
  id: string;
  commitId: string;
  unitId: string;
  taskType: string;
  deterministicContext: Record<string, unknown>;
  retrievedItems: Array<{ source: string; reason: string; tokenCost: number }>;
  tokenBudget: number;
  usedTokens: number;
  compilerVersion: string;
  rankingVersion: string;
  rankingProfile: string;
  estimatorVersion: string;
  candidateCount: number;
  selectedCount: number;
  truncatedCount: number;
  compileLatencyMs: number;
  retrievalAudit: Array<Record<string, unknown>>;
};

export type StoryWorkflowRun = {
  id: string;
  unit_id: string;
  workflow_key: string;
  external_run_id: string | null;
  context_pack_id: string;
  pending_change_id: string | null;
  status: string;
  revision_count: number;
  max_revisions: number;
  created_at_ms: number;
};

export type NarrativePrinciple = {
  id: string;
  layer: "L3";
  knowledgeClass: "descriptive" | "prescriptive" | "theoretical" | "evaluated";
  framework: string;
  claim: string;
  mechanism: string;
  purpose: string;
  conditions: string[];
  applicableFormats: string[];
  applicableGenres: string[];
  risks: string[];
  counterexamples: string[];
  craftControls: string[];
  confidence: number;
  crossWorkEvaluation: null | { workCount: number; result: string; score: number; notes: string };
};

export type ProjectFramework = Pick<NarrativePrinciple, "framework" | "claim" | "mechanism" | "risks" | "counterexamples" | "confidence"> & {
  principleId: string;
  mode: "recommend" | "selected" | "disabled";
  rationale: string;
  updatedAt: number;
};

export type StoryBranch = {
  id: string;
  name: string;
  kind: string;
  headCommitId: string;
  createdFromCommitId: string | null;
  createdAt: number;
};

export type StoryCommit = {
  id: string;
  sequence: number;
  parent_commit_id: string | null;
  author_id: string;
  actor_type: string;
  provenance_json: string;
  schema_version: number;
  created_at_ms: number;
};

export type StudioUnit = {
  id: string;
  parentId: string | null;
  type: string;
  sortOrder: number;
  title: string;
  status: string;
  content: string;
  contentHash: string | null;
  lastAppliedCommitId: string | null;
  draftContent: string | null;
  draftBaseCommitId: string | null;
};

export type PendingChange = {
  id: string;
  unitId: string;
  baseCommitId: string;
  proposedContent: string;
  proposedContentHash: string;
  narrativeDelta: NarrativeDelta;
  provenance: Record<string, unknown>;
  status: "pending";
  createdAt: number;
};

export type StudioSnapshot = {
  project: { id: string; name: string; formatProfile: string };
  branch: { id: string; name: string; kind: string; headCommitId: string };
  units: StudioUnit[];
  selectedUnit: StudioUnit | null;
  pendingChanges: PendingChange[];
};

export type WorldState = {
  facts: Array<Record<string, unknown>>;
  events: Array<Record<string, unknown>>;
  knowledge: Array<Record<string, unknown>>;
  beliefs: Array<Record<string, unknown>>;
  relationships: Array<Record<string, unknown>>;
  promises: Array<Record<string, unknown>>;
};

export type ProductionNode = { id: string; node_type: string; label: string; status: string; content_hash: string };
export type ProductionGraph = {
  id: string;
  source_unit_id: string;
  source_commit_id: string;
  status: "active" | "stale" | "archived";
  nodes: ProductionNode[];
  edges: Array<{ id: string; source_node_id: string; target_node_id: string; dependency_aspect: string }>;
};
export type ProductionAsset = { id: string; asset_type: string; status: string; adapter_key: string; stale_reason: string | null; source_commit_id: string };
export type CollaborationSnapshot = {
  branch: { id: string; name: string; headCommitId: string };
  activeAuthors: Array<Record<string, unknown>>;
  comments: Array<Record<string, unknown>>;
  suggestions: Array<Record<string, unknown>>;
  crdt: { mode: string; canonicalLedgerSeparated: boolean; realtimeTransport: string; updates: Array<Record<string, unknown>> };
};
export type WorldGameSnapshot = {
  boundary: { storyOwns: string[]; gameRuntimeOwns: string[]; gameTickStateStored: boolean };
  npcs: Array<Record<string, unknown>>;
  knowledge: Array<Record<string, unknown>>;
  quests: Array<Record<string, unknown>>;
  dialogueNodes: Array<Record<string, unknown>>;
  dialogueEdges: Array<Record<string, unknown>>;
  events: Array<Record<string, unknown>>;
  unlocks: Array<Record<string, unknown>>;
};

type AcceptResult = { commitId: string; branchId: string; unitId: string; idempotent: boolean };

export class StoryClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;

  constructor(status: number, payload: StoryApiError | null) {
    super(payload?.error.message ?? `Story API request failed (${status}).`);
    this.name = "StoryClientError";
    this.status = status;
    this.code = payload?.error.code ?? "SERVICE_TEMPORARILY_UNAVAILABLE";
    this.retryable = payload?.error.retryable ?? status >= 500;
  }
}

function url(origin: string, path: string): string {
  return `${origin.replace(/\/$/u, "")}${path}`;
}

function headers(auth: StudioAuth, extra: HeadersInit = {}): Headers {
  const result = new Headers(extra);
  if (auth.previewActor) result.set("X-Preview-Actor", auth.previewActor);
  if (auth.previewDisplayName) result.set("X-Preview-Display-Name", auth.previewDisplayName);
  if (auth.branchName) result.set("X-Story-Branch", auth.branchName);
  return result;
}

export function loadBranches(input: { apiOrigin: string; projectId: string; auth: StudioAuth; signal?: AbortSignal }): Promise<StoryBranch[]> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/branches`, input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

export async function loadCommits(input: { apiOrigin: string; projectId: string; auth: StudioAuth; signal?: AbortSignal }): Promise<StoryCommit[]> {
  const result = await request<{ items: StoryCommit[] }>(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/commits`, input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
  return result.items;
}

export function createBranch(input: {
  apiOrigin: string;
  projectId: string;
  name: string;
  kind: "alternative" | "adaptation" | "experiment";
  auth: StudioAuth;
}): Promise<{ branchId: string; name: string; headCommitId: string; createdFromCommitId: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/branches`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: input.name, kind: input.kind }),
  });
}

async function request<T>(origin: string, path: string, auth: StudioAuth, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url(origin, path), {
    ...init,
    credentials: "include",
    headers: headers(auth, init.headers),
  });
  if (!response.ok) {
    let error: StoryApiError | null = null;
    try {
      error = await response.json() as StoryApiError;
    } catch {
      // The HTTP status still provides a useful fallback error.
    }
    throw new StoryClientError(response.status, error);
  }
  const payload = await response.json() as { data: T };
  return payload.data;
}

export function loadProjects(input: { apiOrigin: string; auth: StudioAuth; signal?: AbortSignal }): Promise<StoryProject[]> {
  return request(input.apiOrigin, "/api/v1/projects", input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

export function compileContext(input: {
  apiOrigin: string;
  projectId: string;
  unitId: string;
  taskType: string;
  auth: StudioAuth;
}): Promise<{ id: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/context-packs`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unitId: input.unitId, taskType: input.taskType }),
  });
}

export function inspectContext(input: {
  apiOrigin: string;
  projectId: string;
  contextPackId: string;
  auth: StudioAuth;
}): Promise<ContextPackInspection> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/context-packs/${encodeURIComponent(input.contextPackId)}`, input.auth);
}

export function loadWorkflowRuns(input: {
  apiOrigin: string;
  projectId: string;
  auth: StudioAuth;
}): Promise<StoryWorkflowRun[]> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/ai/runs`, input.auth);
}

export function runStoryWorkflow(input: {
  apiOrigin: string;
  projectId: string;
  unitId: string;
  workflowKey: "story.scene.draft.v1" | "story.scene.revise.v1" | "story.continuity.evaluate.v1" | "story.research.synthesize.v1";
  instruction: string;
  revisionCount: number;
  auth: StudioAuth;
}): Promise<{ id: string; status: string; contextPackId: string; pendingChangeId: string | null; humanAcceptanceRequired: true }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/ai/runs`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      unitId: input.unitId,
      workflowKey: input.workflowKey,
      instruction: input.instruction,
      revisionCount: input.revisionCount,
    }),
  });
}

export function loadNarrativePrinciples(input: { apiOrigin: string; auth: StudioAuth }): Promise<NarrativePrinciple[]> {
  return request(input.apiOrigin, "/api/v1/narrative-intelligence/principles", input.auth);
}

export function loadProjectFrameworks(input: { apiOrigin: string; projectId: string; auth: StudioAuth }): Promise<ProjectFramework[]> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/frameworks`, input.auth);
}

export function setProjectFrameworkPreference(input: {
  apiOrigin: string;
  projectId: string;
  principleId: string;
  mode: "recommend" | "selected" | "disabled";
  rationale: string;
  auth: StudioAuth;
}): Promise<{ principleId: string; mode: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/frameworks`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ principleId: input.principleId, mode: input.mode, rationale: input.rationale }),
  });
}

export function createProject(input: {
  apiOrigin: string;
  name: string;
  formatProfile: "novel" | "screenplay" | "short_drama" | "game_narrative" | "custom";
  constitution: Record<string, unknown>;
  auth: StudioAuth;
}): Promise<{ projectId: string; branchId: string; headCommitId: string }> {
  return request(input.apiOrigin, "/api/v1/projects", input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: input.name, formatProfile: input.formatProfile, constitution: input.constitution }),
  });
}

export function createUnit(input: {
  apiOrigin: string;
  projectId: string;
  expectedHead: string;
  title: string;
  type?: "chapter" | "scene" | "sequence" | "quest" | "dialogue_node";
  sortOrder?: number;
  auth: StudioAuth;
}): Promise<{ unitId: string; commitId: string; branchId: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/units`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": `studio-onboarding:${crypto.randomUUID()}` },
    body: JSON.stringify({
      expectedHead: input.expectedHead,
      type: input.type ?? "scene",
      title: input.title,
      sortOrder: input.sortOrder ?? 0,
    }),
  });
}

export function loadStudio(input: {
  apiOrigin: string;
  projectId: string;
  auth: StudioAuth;
  unitId?: string;
  signal?: AbortSignal;
}): Promise<StudioSnapshot> {
  const query = input.unitId ? `?unitId=${encodeURIComponent(input.unitId)}` : "";
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/studio${query}`, input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

export function loadWorld(input: {
  apiOrigin: string;
  projectId: string;
  auth: StudioAuth;
  signal?: AbortSignal;
}): Promise<WorldState> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/world`, input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

export function loadInvalidations(input: {
  apiOrigin: string;
  projectId: string;
  auth: StudioAuth;
  signal?: AbortSignal;
}): Promise<NarrativeInvalidation[]> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/invalidations`, input.auth, {
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

export function saveDraft(input: {
  apiOrigin: string;
  projectId: string;
  unitId: string;
  baseCommitId: string;
  content: string;
  auth: StudioAuth;
}): Promise<{ contentHash: string; updatedAt: number }> {
  return request(
    input.apiOrigin,
    `/api/v1/projects/${encodeURIComponent(input.projectId)}/units/${encodeURIComponent(input.unitId)}/draft`,
    input.auth,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ baseCommitId: input.baseCommitId, content: input.content }),
    },
  );
}

export function proposeDraft(input: {
  apiOrigin: string;
  projectId: string;
  unitId: string;
  baseCommitId: string;
  content: string;
  auth: StudioAuth;
}): Promise<{ changeId: string; status: "pending"; baseCommitId: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/changes`, input.auth, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      unitId: input.unitId,
      baseCommitId: input.baseCommitId,
      proposedContent: input.content,
      narrativeDelta: {},
      provenance: { source: "human", editor: "story-studio" },
    }),
  });
}

export function acceptPendingChange(input: {
  apiOrigin: string;
  projectId: string;
  changeId: string;
  expectedHead: string;
  auth: StudioAuth;
}): Promise<AcceptResult> {
  return request(
    input.apiOrigin,
    `/api/v1/projects/${encodeURIComponent(input.projectId)}/changes/${encodeURIComponent(input.changeId)}/accept`,
    input.auth,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": `studio-accept:${input.changeId}`,
      },
      body: JSON.stringify({ expectedHead: input.expectedHead }),
    },
  );
}

export function rejectPendingChange(input: {
  apiOrigin: string;
  projectId: string;
  changeId: string;
  auth: StudioAuth;
}): Promise<{ changeId: string; status: "rejected" }> {
  return request(
    input.apiOrigin,
    `/api/v1/projects/${encodeURIComponent(input.projectId)}/changes/${encodeURIComponent(input.changeId)}/reject`,
    input.auth,
    { method: "POST" },
  );
}

export function decideInvalidation(input: {
  apiOrigin: string;
  projectId: string;
  invalidationId: string;
  status: "acknowledged" | "resolved" | "dismissed";
  auth: StudioAuth;
}): Promise<NarrativeInvalidation> {
  return request(
    input.apiOrigin,
    `/api/v1/projects/${encodeURIComponent(input.projectId)}/invalidations/${encodeURIComponent(input.invalidationId)}`,
    input.auth,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: input.status }),
    },
  );
}

export function buildProductionGraph(input: { apiOrigin: string; projectId: string; unitId: string; auth: StudioAuth }): Promise<ProductionGraph> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/production/graphs`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ unitId: input.unitId }),
  });
}

export function loadProductionAssets(input: { apiOrigin: string; projectId: string; auth: StudioAuth }): Promise<ProductionAsset[]> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/production/assets`, input.auth);
}

export function registerProductionAsset(input: {
  apiOrigin: string; projectId: string; graphId: string; nodeId: string; assetType: "storyboard" | "image" | "video";
  adapterKey: string; dependencyAspects: string[]; auth: StudioAuth;
}): Promise<{ id: string; status: string; generatedContent: false }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/production/assets`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
  });
}

export function requestProductionTts(input: {
  apiOrigin: string; projectId: string; unitId: string; voiceId: string; locale: string; speed: number; style: string; auth: StudioAuth;
}): Promise<{ id: string; status: string; cacheHit: boolean }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/production/tts`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
  });
}

export function exportProduction(input: { apiOrigin: string; projectId: string; format: "json" | "markdown" | "fountain" | "production_manifest"; auth: StudioAuth }): Promise<{ id: string; format: string; sourceCommitId: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/exports`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ format: input.format }),
  });
}

export function loadCollaboration(input: { apiOrigin: string; projectId: string; auth: StudioAuth }): Promise<CollaborationSnapshot> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/collaboration`, input.auth);
}

export function acquireLease(input: { apiOrigin: string; projectId: string; unitId: string; auth: StudioAuth }): Promise<{ leaseToken: string; expiresAt: number }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/collaboration/leases`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ unitId: input.unitId, leaseSeconds: 120 }),
  });
}

export function appendStudioCrdtUpdate(input: {
  apiOrigin: string; projectId: string; unitId: string; headCommitId: string; leaseToken: string; auth: StudioAuth;
}): Promise<{ id: string; canonicalMutation: false }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/collaboration/crdt-updates`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json", "X-Story-Lease-Token": input.leaseToken },
    body: JSON.stringify({ unitId: input.unitId, clientId: "story-studio", clientSequence: Date.now(), baseCommitId: input.headCommitId,
      update: { kind: "presence-checkpoint", unitId: input.unitId, timestamp: Date.now() } }),
  });
}

export function addStudioComment(input: { apiOrigin: string; projectId: string; unitId: string; body: string; auth: StudioAuth }): Promise<{ id: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/collaboration/comments`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ unitId: input.unitId, body: input.body, anchor: { kind: "unit" } }),
  });
}

export function loadWorldGame(input: { apiOrigin: string; projectId: string; auth: StudioAuth }): Promise<WorldGameSnapshot> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/world-game`, input.auth);
}

export function createStudioQuest(input: { apiOrigin: string; projectId: string; title: string; eventType: string; auth: StudioAuth }): Promise<{ id: string }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/world-game/quests`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title: input.title, definition: { objective: "author_review_required" }, unlockEventType: input.eventType }),
  });
}

export function ingestStudioNarrativeEvent(input: { apiOrigin: string; projectId: string; eventType: string; auth: StudioAuth }): Promise<{ duplicate: boolean; unlocks: Array<Record<string, unknown>>; gameStateMutated: false }> {
  return request(input.apiOrigin, `/api/v1/projects/${encodeURIComponent(input.projectId)}/narrative-events`, input.auth, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source: "story-studio-simulator", externalEventId: `studio-event-${crypto.randomUUID()}`, eventType: input.eventType,
      payload: { simulation: true, authorReviewRequired: true } }),
  });
}
